import { useEffect } from "react";
import type { Media } from "@/types/media";

interface PlaybackModalProps {
  media: Media;
  open: boolean;
  onClose: () => void;
}

export default function PlaybackModal({ media, open, onClose }: PlaybackModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background-950/85 p-3 backdrop-blur-sm md:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl animate-scale-in overflow-hidden rounded-lg border border-background-200/60 bg-background-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-background-200/60 px-4 py-3 md:px-5">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950 md:text-base">
            <i className="ri-play-circle-fill text-primary-500"></i>
            {media.title} · 播放演示
          </h3>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="relative w-full overflow-hidden pt-[56.25%]">
          <img
            src={media.backdrop}
            alt={`${media.title} 演示画面`}
            className="absolute inset-0 h-full w-full object-cover object-center opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/40 to-transparent"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 md:h-20 md:w-20">
              <i className="ri-play-fill text-3xl md:text-4xl"></i>
            </span>
            <p className="mt-5 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              演示模式，暂不支持真实播放
            </p>
            <p className="mt-2 max-w-md text-xs leading-relaxed text-foreground-500 md:text-sm">
              本站影视播放为前端功能演示，不接入真实流媒体资源，也不提供下载与会员服务。影片信息仅用于展示与浏览。
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-background-200/60 px-4 py-3 text-xs text-foreground-500 md:flex-row md:items-center md:justify-between md:px-5">
          <span className="flex items-center gap-1.5">
            <i className="ri-information-line text-accent-500"></i>
            当前为演示层，点击其他入口同理
          </span>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            我知道了
          </button>
        </div>
      </div>
    </div>
  );
}